import { ShieldCheck, PaintBucket, Radar, FileBadge } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BIZ } from "../data/site";
import { Img } from "../components/Img";

const CREDENTIALS = [
  { icon: ShieldCheck, t: "I-CAR Gold Class", d: "The collision industry's highest training tier, held by roughly 10% of shops. Our technicians train continuously on advanced materials, structural systems, and vehicle electronics." },
  { icon: PaintBucket, t: "Sherwin-Williams Certified Refinishing", d: "Computerized spectrophotometer color matching with OEM-grade refinish systems, sprayed and cured in our time-efficient down-draft booth." },
  { icon: Radar, t: "ADAS Calibration Certified", d: "Factory-procedure recalibration of cameras, radars, and sensors after collision, glass, or suspension work — documented with verification reports." },
  { icon: FileBadge, t: "Written Lifetime Warranty", d: "Every repair carries a signed, written warranty on workmanship and paint for as long as you own the vehicle. Not a promise — a document." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Westway Collision Center — Carrollton, TX Body Shop"
        description="I-CAR Gold Class collision center in Carrollton, TX specializing in luxury & exotic vehicles. Frame machine, ADAS, lifetime warranty. (214) 888-2886."
        path="/about"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />

      <section className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />
            <Eyebrow>The Standard, Not The Exception</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance">
              About Westway Collision Center in Carrollton, TX
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              Westway Collision Center is a certified collision repair facility at {BIZ.addressLine},
              built around one conviction: a repaired vehicle should be indistinguishable from one
              that was never damaged — structurally, cosmetically, and electronically. We specialize
              in luxury and exotic automobiles, and we back that claim with training credentials,
              measurement data, and a written lifetime warranty on every job.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
              <Img name="westway-building-dodge-ram" alt="Westway Collision Center building at 1420 Westway Cir, Carrollton, TX" eager className="h-64 sm:h-80 w-full object-cover" sizes="(min-width: 1024px) 40vw, 100vw" />
            </div>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">Why We Exist</h2>
            <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
              Most collision shops are built for volume: get the car in, get it out, hope nobody looks
              too closely. We built Westway for the opposite — for the owner who reads the estimate,
              asks about the frame numbers, and knows what their car is worth. Our clientele drives
              Ferrari, Bentley, Lamborghini, Aston Martin, Porsche, McLaren, Mercedes-AMG, and BMW M
              vehicles, and the discipline those cars demand shapes how we repair every vehicle that
              enters the building.
            </p>
            <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
              That means OEM repair procedures followed to the letter, structure verified on a
              computerized frame machine with printed before-and-after data, color matched by
              spectrophotometer rather than by eye, and every driver-assistance system recalibrated
              to factory specification before delivery.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-9 transition-colors duration-500 hover:border-accent/30">
                  <c.icon size={28} className="text-accent" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-4 font-display text-xl font-semibold text-slate-100">{c.t}</h3>
                  <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-8">
            <Eyebrow>Inside Westway</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">The Shop, The Cars, The Owners</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ["westway-collision-shop-interior", "Shop interior and refinishing area at Westway Collision Center in Carrollton, TX"],
              ["rolls-royce-spectre-service-bay", "Rolls-Royce Spectre in the service bay at Westway Collision Center in Carrollton, TX"],
              ["happy-customer-westway-collision", "Happy customer taking delivery at Westway Collision Center in Carrollton, TX"],
              ["customer-chevrolet-tahoe-westway", "Customer with a finished Chevrolet Tahoe at Westway Collision Center in Carrollton, TX"],
            ].map(([n, a], i) => (
              <Reveal key={n} delay={i * 0.05}>
                <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
                  <Img name={n} alt={a} className="h-44 sm:h-56 w-full object-cover" sizes="(min-width: 1024px) 25vw, 50vw" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock heading="Come See the Difference" sub={`Visit us at ${BIZ.addressLine} — or call ${BIZ.phoneDisplay} and talk directly to the people who will repair your vehicle.`} />
        </div>
      </section>
    </div>
  );
}
